import { PayloadAction } from '@reduxjs/toolkit'
import { IActionPersonRequest, PersonTypes } from './types'
import { PersonActions } from './index'
import { all, apply, put, race, take, takeLatest } from 'redux-saga/effects'
import { PersonWork } from '../../assets/data/mock-data-person'
import personService from '../../services/person.service'
import { SnackbarActions, SnackBarMessageType } from '../snackbar'
import Messages from '../../assets/locales/pt/translation.json'
import { PersonIntegrated } from '../../application/domain/models/entity/person.integrated'
import addressService from '../../services/address.service'
import {IFiltersAddress} from "../address/types"
import {VehicleIntegrated} from "../../application/domain/models/entity/vehicle.integrated"
import {Address} from "../../application/domain/models/entity/address"
import {Contact} from "../../application/domain/models/entity/contact"
// import { format, subDays } from 'date-fns'

/**
 * <h5>Generator functions that make calls to the authentication service to obtain the data.</h5>
 * @see {@link https://developer.mozilla.org/pt-BR/docs/Web/JavaScript/Reference/Statements/function*}
 * @typedef PersonSagas
 * @namespace PersonSagas
 * @category React
 * @subcategory Redux / Sagas
 */

/**
 * Generating function that calls the login service to authenticate the system.
 * @memberof PersonSagas
 * @alias PersonSagas.request
 * @function
 * @category React
 * @subcategory Redux / Sagas
 * @param {PayloadAction<IActionPersonRequest>} action User triggered action data.
 */
export function* request(action: PayloadAction<IActionPersonRequest>) {
    const { personSuccess, personFailure } = PersonActions
    const { openSnackBar } = SnackbarActions
    try {
        const { filters, paginator } = action.payload
/*        if (!filters?.start_date && !filters?.end_date) {
            const today = new Date()
            filters.end_date = format(today, 'yyyy-MM-dd')
            filters.start_date = format(subDays(today, 90), 'yyyy-MM-dd')
        }*/

        // @ts-ignore
        const { data: filteredUsers, cancelled } = yield race({
            data: apply(personService, personService.getPerson, [filters, paginator]),
            cancelled: take(PersonTypes.PERSON_CANCEL)
        })

        if (cancelled) return

        yield put(personSuccess({
            data: filteredUsers
        }))
    } catch (err: any) {
        yield put(personFailure())
        if (err.response.status >= 400 && err.response.status <= 499) {
            if (err.response.data.message === 'Required filter was not provided...') {
                yield put(openSnackBar({
                    title: 'Validação.',
                    type: SnackBarMessageType.WARNING,
                    message: `${Messages.REQUEST.PERSON.REQUEST.NULL_FILTERS}`
                }))
            }
            if (err.response.data.description === 'cpf must be a valid string of digits!') {
                yield put(openSnackBar({
                    title: 'Validação.',
                    type: SnackBarMessageType.WARNING,
                    message: `${Messages.REQUEST.PERSON.REQUEST.CPF_NULL}`
                }))
            }
            if (err.response.data.description === 'name must be a string!') {
                yield put(openSnackBar({
                    title: 'Validação.',
                    type: SnackBarMessageType.WARNING,
                    message: `${Messages.REQUEST.PERSON.REQUEST.NAME_NOT_STRING}`
                }))
            }
        }
        if (err.response.status >= 500 && err.response.status <= 599) {
            yield put(openSnackBar({
                title: 'Erro Interno.',
                type: SnackBarMessageType.ERROR,
                message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
            }))
        }
    }
}

/**
 * Generating function that calls the login service to authenticate the system.
 * @memberof PersonSagas
 * @alias PersonSagas.describe
 * @function
 * @category React
 * @subcategory Redux / Sagas
 * @param {PayloadAction<IActionPersonRequest>} action User triggered action data.
 */
export function* describePersonData(action: PayloadAction<IActionPersonRequest>) {
    const { describeSuccess, describeFailure } = PersonActions
    const { openSnackBar } = SnackbarActions
    const { filters } = action.payload

    try {
        // @ts-ignore
        const person: Array<PersonIntegrated> = yield apply(personService, personService.getPersonByDocument, [filters?.cpf])
        const personDatas: Array<any> = []
        const personAutos: Array<any> = []
        const personAddress: Array<any> = []
        const personContacts: Array<any> = []

        for (const data of person) {
            if (data) {
                personDatas.push({
                    name: data.personal_data?.name,
                    cpf: data.personal_data?.cpf,
                    rg: data.personal_data?.rg,
                    birthDate: data.personal_data?.birth_date,
                    municipality: data.personal_data?.municipality,
                    uf: data.personal_data?.uf,
                    updated_at: data.personal_data?.updated_at,
                    origin: data?.origin
                })

                if (data.vehicles?.length) {
                    personAutos.push(...data.vehicles.map((vehicle: VehicleIntegrated) => ({
                        origin: data.origin,
                        ...vehicle
                    })))
                }

                if (data.addresses?.length) {
                    personAddress.push(...data.addresses.map((address: Address) => ({
                        origin: data.origin,
                        ...address
                    })))
                }

                if (data.contacts?.length) {
                    // eslint-disable-next-line
                    personContacts.push(...data.contacts.map((contact: Contact) => {
                        if (contact.value && contact.type && contact.created_at && contact.updated_at) {
                            return {
                                origin: data.origin,
                                ...contact,
                            }
                        }
                    }).filter(Boolean)) // Para remover contatos undefined
                }
            }
        }


        yield put(describeSuccess({
            data: {
                name: person.find(o => o.personal_data)?.personal_data?.name || '',
                cpf: person.find(o => o.personal_data)?.personal_data?.cpf || '',
                personDatas,
                personWorks: PersonWork,
                personAutos,
                personAddress,
                personContacts,
            }
        }))
    } catch (err: any) {
        yield put(describeFailure({
            data: {
                cpf: filters?.cpf,
                name: 'Não Encontrado.'
            }
        }))
        if (err?.response?.status >= 400 && err?.response?.status <= 499) {
            yield put(openSnackBar({
                title: 'Registro não encontrado.',
                type: SnackBarMessageType.WARNING,
                message: `${Messages.REQUEST.PERSON.DESCRIBE.INVALID}`
            }))
        } else if (err?.response?.status >= 500 && err?.response?.status <= 599) {
            yield put(openSnackBar({
                title: 'Erro Interno.',
                type: SnackBarMessageType.ERROR,
                message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
            }))
        }
    }
}

export function* describeAddress(action: PayloadAction<IActionPersonRequest>) {
    const { addressSuccess, addressFailure } = PersonActions
    const { openSnackBar } = SnackbarActions
    const { filters } = action.payload

    try {
        // @ts-ignore
        const person: Array<PersonIntegrated> = yield apply(personService, personService.getPersonByDocument, [filters?.cpf])
        const locations = []

        for (const { addresses, origin } of person) {
            if (addresses) {
                for (const address of addresses) {
                    const addressFilters: IFiltersAddress = {
                        street: address.street,
                        start_date: filters?.start_date,
                        end_date: filters?.end_date
                    }
                    // @ts-ignore
                    const location = yield apply(addressService, addressService.getAddressPerson, [addressFilters])
                    locations.push({
                        // @ts-ignores
                        origin,
                        // @ts-ignore
                        location,
                        // @ts-ignore
                        street: address.street
                    })
                }
            }
        }

        yield put(addressSuccess({
            data: locations
        }))
    } catch (err: any) {
        yield put(addressFailure())
        if (err?.response?.status >= 400 && err?.response?.status <= 499) {
            yield put(openSnackBar({
                title: 'Registro não encontrado.',
                type: SnackBarMessageType.WARNING,
                message: `${Messages.REQUEST.PERSON.DESCRIBE.INVALID}`
            }))
        } else if (err?.response?.status >= 500 && err?.response?.status <= 599) {
            yield put(openSnackBar({
                title: 'Erro Interno.',
                type: SnackBarMessageType.ERROR,
                message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
            }))
        }
    }
}


const personSaga = function* () {
    yield all([
        takeLatest(PersonTypes.PERSON_REQUEST, request),
        takeLatest(PersonTypes.DESCRIBE_REQUEST, describePersonData),
        takeLatest(PersonTypes.ADDRESS_REQUEST, describeAddress)
    ])
}

export default personSaga
